import { useReactiveVar } from '@apollo/client';
import AddIcon from '@mui/icons-material/Add';
import EditIcon from '@mui/icons-material/Edit';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import QueueMusicIcon from '@mui/icons-material/QueueMusic';
import Accordion from '@mui/material/Accordion';
import AccordionDetails from '@mui/material/AccordionDetails';
import AccordionSummary from '@mui/material/AccordionSummary';
import { alpha, type Theme } from '@mui/material/styles';
import Typography from '@mui/material/Typography';
import makeStyles from '@mui/styles/makeStyles';
import type React from 'react';
import EditCollectionDetails from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/EditCollectionDetails/EditCollectionDetails';
import MyCollectionDetails from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/MyCollectionDetails/MyCollectionDetails';
import NewCollectionDetails from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/NewCollectionDetails/NewCollectionDetails';
import usePlaylistItemStyles from 'src/HomePage/DrawerAndPushedContents/RightSideDrawer/playlistItemStyles';
import viewStateVar from 'src/store/viewState';

const useStyles = makeStyles((theme: Theme) => ({
  root: {
    width: '100%',
    height: '100%',
    display: 'flex',
    flexDirection: 'column',
    overflowY: 'auto',
    overflowX: 'hidden',
    padding: 0,
    '& .MuiAccordion-root': {
      backgroundColor: 'transparent',
      backgroundImage: 'none',
      boxShadow: 'none',
      margin: 0,
      '&:before': {
        display: 'none',
      },
    },
    '& .MuiAccordionSummary-root': {
      backgroundColor: 'transparent',
      minHeight: 48,
      '&.Mui-expanded': {
        minHeight: 48,
      },
    },
    '& .MuiAccordionSummary-content': {
      margin: '10px 0',
      '&.Mui-expanded': {
        margin: '10px 0',
      },
    },
  },
  heading: {
    fontSize: 13,
    fontWeight: 700,
    letterSpacing: '1.2px',
    textTransform: 'uppercase' as const,
    display: 'flex',
    alignItems: 'center',
    cursor: 'pointer',
    transition: 'color 150ms',
  },
  headingNew: {
    color: theme.palette.secondary.main,
  },
  headingEdit: {
    color: theme.palette.warning.main,
  },
  headingMy: {
    color: theme.palette.info.main,
  },
  accordionSummary: {
    padding: '0 12px',
  },
  accordionDetails: {
    padding: '4px 0 12px',
  },
  sectionIcon: {
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: 28,
    height: 28,
    borderRadius: 7,
    marginRight: theme.spacing(1.25),
    flexShrink: 0,
  },
  sectionIconNew: {
    backgroundColor: alpha(theme.palette.secondary.main, 0.19),
    color: theme.palette.secondary.main,
  },
  sectionIconEdit: {
    backgroundColor: alpha(theme.palette.warning.main, 0.19),
    color: theme.palette.warning.main,
  },
  sectionIconMy: {
    backgroundColor: alpha(theme.palette.info.main, 0.19),
    color: theme.palette.info.main,
  },
  sectionDivider: {
    height: 1,
    backgroundColor: theme.palette.divider,
    margin: '4px 12px',
    flexShrink: 0,
  },
  expandedSection: {
    flex: 1,
    display: 'flex',
    flexDirection: 'column',
    minHeight: 0,
    overflow: 'hidden',
    '& .MuiAccordion-root': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    '& .MuiCollapse-root.MuiCollapse-entered': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
      // MUI Collapse sets height: auto inline when expanded — must override
      height: '100% !important',
    },
    '& .MuiCollapse-wrapper, & .MuiCollapse-wrapperInner': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    // MUI Accordion wraps content in a region div inside the Collapse wrappers
    '& .MuiAccordion-region, & [role="region"]': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
    '& .MuiAccordionDetails-root': {
      flex: 1,
      display: 'flex',
      flexDirection: 'column',
      minHeight: 0,
    },
  },
  collapsedSection: {
    flexShrink: 0,
  },
}));

const CollectionsDrawer: React.FC = () => {
  const classes = useStyles();
  const sharedClasses = usePlaylistItemStyles();
  const viewState = useReactiveVar(viewStateVar);

  const newCollectionClickHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      openNewCollection: !viewState.openNewCollection,
      openEditCollection: false,
      openMyCollection: false,
      renamePlaylist: null,
    });
  };

  const editCollectionClickHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      openEditCollection: !viewState.openEditCollection,
      openNewCollection: false,
      openMyCollection: true,
      renamePlaylist: null,
    });
  };

  const myCollectionClickHandler = (): void => {
    viewStateVar({
      ...viewStateVar(),
      openMyCollection: !viewState.openMyCollection,
      openNewCollection: false,
      openEditCollection: false,
      renamePlaylist: null,
    });
  };

  const renderNewPlaylistSection = (): React.ReactNode => (
    <Accordion expanded={viewState.openNewCollection}>
      <AccordionSummary
        onClick={newCollectionClickHandler}
        classes={{ root: classes.accordionSummary }}
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        id="panel1a-header"
      >
        <Typography className={`${classes.heading} ${classes.headingNew}`}>
          <span className={`${classes.sectionIcon} ${classes.sectionIconNew}`}>
            <AddIcon sx={{ fontSize: 17 }} />
          </span>
          New Playlist
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.accordionDetails }}>
        <NewCollectionDetails />
      </AccordionDetails>
    </Accordion>
  );

  const renderEditPlaylistSection = (): React.ReactNode => (
    <Accordion expanded={viewState.openEditCollection}>
      <AccordionSummary
        onClick={editCollectionClickHandler}
        classes={{ root: classes.accordionSummary }}
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        id="panel3a-header"
      >
        <Typography className={`${classes.heading} ${classes.headingEdit}`}>
          <span className={`${classes.sectionIcon} ${classes.sectionIconEdit}`}>
            <EditIcon sx={{ fontSize: 17 }} />
          </span>
          Edit Playlist
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.accordionDetails }}>
        <EditCollectionDetails />
      </AccordionDetails>
    </Accordion>
  );

  const renderMyPlaylistsSection = (): React.ReactNode => (
    <Accordion expanded={viewState.openMyCollection}>
      <AccordionSummary
        onClick={myCollectionClickHandler}
        classes={{ root: classes.accordionSummary }}
        expandIcon={<ExpandMoreIcon sx={{ fontSize: 18, color: 'text.secondary' }} />}
        id="panel2a-header"
      >
        <Typography id="cyp-my-playlists" className={`${classes.heading} ${classes.headingMy}`}>
          <span className={`${classes.sectionIcon} ${classes.sectionIconMy}`}>
            <QueueMusicIcon sx={{ fontSize: 17 }} />
          </span>
          My Playlists
        </Typography>
      </AccordionSummary>
      <AccordionDetails classes={{ root: classes.accordionDetails }}>
        <MyCollectionDetails />
      </AccordionDetails>
    </Accordion>
  );

  const newIsOpen = viewState.openNewCollection;
  const editIsOpen = viewState.openEditCollection;

  if (editIsOpen) {
    return (
      <div className={`${classes.root} ${sharedClasses.thinScrollbar}`}>
        <div className={classes.collapsedSection}>
          {renderNewPlaylistSection()}
          <div className={classes.sectionDivider} />
        </div>
        <div className={classes.expandedSection}>{renderEditPlaylistSection()}</div>
        <div className={classes.collapsedSection}>
          <div className={classes.sectionDivider} />
          {renderMyPlaylistsSection()}
        </div>
      </div>
    );
  }

  return (
    <div className={`${classes.root} ${sharedClasses.thinScrollbar}`}>
      <div className={newIsOpen ? classes.expandedSection : classes.collapsedSection}>{renderNewPlaylistSection()}</div>
      <div className={classes.collapsedSection}>
        <div className={classes.sectionDivider} />
        {renderMyPlaylistsSection()}
      </div>
    </div>
  );
};

export default CollectionsDrawer;
